import { Document } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { BUTTON_SIZE, BUTTON_TYPES, Button, Icon, ProductInfo, ReadMore } from '@src/quince-components/src'
import { If } from '@src/quince-components/src/atoms/conditionals'
import { renderErrorMessage } from '@src/quince-components/src/atoms/productForm/ProductForm'
import SizeChart from '@src/quince-components/src/atoms/sizeChart'
import { SizeGuide } from '@src/quince-components/src/atoms/sizeGuide'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import ContentfulProductMeasuringGuide from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { CustomAttribute } from '@src/quince-site-common/src/domains/shopify/CustomAttribute'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getFormattedCustomFieldText,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import { getVarientId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { useBuyNowPDP } from '@src/quince-site-common/src/hooks/cart/hooks/use-buy-now-pdp'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import BundleProductCard from '@src/quince-site-pdp/src/molecules/bundleProductCard/BundleProductCard'
import AddToCart from '@src/quince-site-pdp/src/molecules/floatingAddToCartSection/AddToCart'
import { getMtoTextDocument } from '@src/quince-site-pdp/src/molecules/productFormWrapper/ProductFormWrapper'
import DiscountIcon from '@src/quince-site-pdp/src/organisms/icons/promo_Icon.inline.svg'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { stripHtml } from '../../../../../quince-site-common/src/organisms/appLayout/molecules/cartPopUp/cart.helper'
import BundleProductImages from '../productImages/BundleProductImages'

import {
  checkSingleVariantSelectedForEachProduct,
  getBundleInitialState,
  getProductsPurchaseInfo,
} from './BundleProductPurchase.helpers'
import styles from './BundleProductPurchase.module.scss'
import { BundleActionType } from './BundleProductPurchase.types'
import bundleReducer from './BundleProductState'

const currency = '$'

const BundleProductPurchase = (props: BundlePageSectionProps) => {
  const mergeProducts = props.mergeProducts || []
  const customBundleProducts = props.bundle.customBundleProducts || []
  const addOnBundleProducts = props.bundle.addOnBundleProducts || []
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const searchParams = useSearchParams()
  const [bundleConfig, dispatch] = useReducer(bundleReducer, {}, () =>
    getBundleInitialState(props.bundle, searchParams.toString()),
  )

  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  const addToCartRef = useRef<HTMLDivElement | null>(null)
  const [intersectRef, isIntersecting] = useIntersect(null, '0px', 0, false)

  const discount = props.bundle.discountPrice || 0

  const { shopifyProductMap } = useMemo(() => {
    const shopifyProductMap: Record<number, Product> = {}
    ;[...customBundleProducts, ...addOnBundleProducts, ...mergeProducts].forEach(product => {
      shopifyProductMap[product.shopifyProductId] = product
    })

    return { shopifyProductMap }
  }, [props.bundle.contentfulProductId])

  const {
    addMultipleToCart,
    isActionInProgress: isAddToCartInProgress,
    errorMessage: errorMessageComingFromShopify,
    buyNowCart,
    isBuyNowInProgress,
  } = useCart()

  const [sizeChartConfig, setSizeChartConfig] = useState<{
    show: boolean
    sizeChart?: ContentfulSizeChart
    mergedSizeChart?: MergedSizeChart[]
    measuringGuides?: ContentfulProductMeasuringGuide[]
  } | null>(null)

  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const totalPrice = Object.values([...bundleConfig.products, ...bundleConfig.optionalProducts]).reduce(
    (sum, config) => {
      if (!config.selection.isSelected) {
        return sum
      }
      return sum + config.salePrice
    },
    0,
  )

  const traditionalPrice = Object.values([...bundleConfig.products, ...bundleConfig.optionalProducts]).reduce(
    (sum, config) => {
      if (!config.selection.isSelected) {
        return sum
      }
      return sum + config.traditionalPrice
    },
    0,
  )

  const traditionalDiscount = useMemo(() => {
    const _traditionalDiscount = productHelper.calculateDiscount(traditionalPrice, totalPrice)
    return _traditionalDiscount ? Math.floor(+formatDisplayAmountValue(_traditionalDiscount)) : null
  }, [props.bundle.contentfulProductId, bundleConfig])

  const bundlePrice = getDiscountedPrice(totalPrice, discount)

  const isSingleVariantSelectedForEachProduct = checkSingleVariantSelectedForEachProduct(bundleConfig)

  const onOptionSelect = async (index: number, optionName: string, optionValue: string, isOptional: boolean) => {
    dispatch({
      type: BundleActionType.UPDATE_SELECTED_OPTIONS,
      payload: { index, optionName, optionValue, isOptional },
    })
  }

  const handleSiblingOptionSelect = async (
    index: number,
    siblingShopifyProductIdToSelect: number,
    isAddOnProduct = false,
  ) => {
    const newSiblingProduct = shopifyProductMap[siblingShopifyProductIdToSelect]
    if (!newSiblingProduct) {
      console.error('Unable to find the selected sibling product')
      return
    }

    try {
      const purchaseInfo = await getProductsPurchaseInfo([newSiblingProduct])
      dispatch({
        type: BundleActionType.UPDATE_SIBLING_SELECTED_OPTIONS,
        payload: {
          index,
          product: newSiblingProduct,
          purchaseInfo: purchaseInfo[newSiblingProduct.id],
          isOptional: isAddOnProduct,
          searchParams: searchParams.toString(),
        },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleAddOnProductChecked = (index: number, checked: boolean) => {
    dispatch({ type: BundleActionType.UPDATE_SELECTION, payload: { index, checked } })
  }

  const handleAddToCart = async (isBuyNow = false) => {
    if (!isSingleVariantSelectedForEachProduct) {
      for (const config of [...bundleConfig.products, ...bundleConfig.optionalProducts]) {
        if (config.selection.isSelected && config.variants.length !== 1) {
          setErrorMsg(`Please select a size for ${config.product.title}`)
          isMobile &&
            addToCartRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest',
            })
          break
        }
      }
      return
    } else {
      setErrorMsg(null)
    }

    const addToCartConfig: {
      product: Product
      selectedOptions: ProductVariantOption[]
      quantity?: number
      customAttributes: CustomAttribute[]
    }[] = []

    const selectedOptionalProducts = bundleConfig.optionalProducts.filter(
      productConfig => productConfig.selection.isSelected,
    )

    const selectedProductsConfig = [...bundleConfig.products, ...selectedOptionalProducts]

    let allItemsInStock = true

    for (const config of selectedProductsConfig) {
      const customAttributes = []
      let shippingInfoText = productHelper.getOrderSummaryText(config.product)
      if (!shippingInfoText) shippingInfoText = productHelper.getShippingInfoText(config.product)
      const productPurchaseInfo = config.purchaseInfo
      const variants = config.variants

      const selectedVariantPurchaseInfo: {
        showMadeToOrder: boolean
        madeToOrderText: Document | undefined
      } = {
        showMadeToOrder: false,
        madeToOrderText: undefined,
      }
      const availableInventory =
        productPurchaseInfo?.shopifyVariantIdVsShopifyVariantInfo[getVarientId(variants[0])]?.quantityAvailable || 0

      if (availableInventory > 0) {
        if (productPurchaseInfo?.shopifyVariantIdShowMTOMap[getVarientId(variants[0])]) {
          selectedVariantPurchaseInfo.showMadeToOrder = true
          const mtoText = getFormattedCustomFieldText(
            productHelper.getCustomFieldValue(config.product, CUSTOM_FIELD_NAMES.MADE_TO_ORDER_TEXT) || '',
          )
          selectedVariantPurchaseInfo.madeToOrderText = mtoText ? getMtoTextDocument(String(mtoText)) : undefined
        }
        setErrorMsg(null)
      } else {
        allItemsInStock = false
        setErrorMsg('Some of the selected items are out of stock')
        isMobile &&
          addToCartRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          })
        break
      }
      if (selectedVariantPurchaseInfo.showMadeToOrder) {
        const mtoText = productHelper.getMTOText(config.product, selectedVariantPurchaseInfo.madeToOrderText)
        customAttributes.push(
          new CustomAttribute({
            key: 'Made to Order',
            value: stripHtml(String(mtoText)),
          }),
        )
      }
      if (shippingInfoText) {
        customAttributes.push(
          new CustomAttribute({
            key: 'Shipping Info',
            value: stripHtml(String(shippingInfoText)),
          }),
        )
      }
      addToCartConfig.push({
        product: config.product,
        selectedOptions: config.selectedOptions,
        quantity: 1,
        customAttributes: customAttributes,
      })
    }

    if (isBuyNow && buyNowCart) {
      await buyNowCart(addToCartConfig)
      return
    }

    if (allItemsInStock && addMultipleToCart) {
      await addMultipleToCart(addToCartConfig, 'pdp_page')
    }
  }

  function showSizeChart(
    sizeChart?: ContentfulSizeChart,
    mergedSizeChart?: MergedSizeChart[],
    measuringGuides?: ContentfulProductMeasuringGuide[],
  ) {
    setSizeChartConfig({
      show: true,
      sizeChart: sizeChart,
      mergedSizeChart: mergedSizeChart,
      measuringGuides: measuringGuides,
    })
  }

  function hideSizeChart() {
    setSizeChartConfig(null)
  }

  useEffect(() => {
    if (!bundleConfig?.products?.[0]?.product) return

    const shopifyId = bundleConfig.products[0].product.shopifyProductId
    const selectedOptions = bundleConfig.products[0].selectedOptions
    const selectedOptionsForBundleProducts: {
      product: Product
      selectedOptions: ProductVariantOption[]
    }[] = []
    bundleConfig.products.forEach(productConfig =>
      selectedOptionsForBundleProducts.push({
        product: productConfig.product,
        selectedOptions: productConfig.selectedOptions,
      }),
    )
    pdpStateManager.setSelectedOptions({
      slug: props.bundle.slug,
      shopifyProductId: shopifyId,
      selectedOptions: selectedOptions.map(so => ({ name: so.name, value: so.value })),
      bundleSelectedOptions: selectedOptionsForBundleProducts,
    })

    pdpStateManager.setSeoPrice({
      seoPrice: Number(bundlePrice),
    })
  }, [bundleConfig])

  const showBuyNowButton = useBuyNowPDP(props.bundle)

  const updateBundleConfig = async () => {
    const purchaseInfo = await getProductsPurchaseInfo([
      ...customBundleProducts,
      ...addOnBundleProducts,
      ...mergeProducts,
    ])

    dispatch({
      type: BundleActionType.UPDATE_PRICING_INFO_FOR_ALL,
      payload: { purchaseInfo: purchaseInfo },
    })
  }

  useEffect(() => {
    updateBundleConfig()
  }, [props.bundle.id, props.bundle])

  const showFloatingAddToCartMobile = isMobile && isSingleVariantSelectedForEachProduct && !isIntersecting

  return (
    <div className={styles.bundlesImagesAndPurchase} ref={intersectRef}>
      <div className={styles.bundleImagesWrapper}>
        <div className={classNames(styles.bundleImages, !isScrollingDown && styles.bundleImages__scrollingUp)}>
          <BundleProductImages
            bundle={props.bundle}
            selectedColor={
              bundleConfig.products[0]?.selectedOptions.find(option => option.name === ProductOptionName.Color)
                ?.value || ''
            }
          />
        </div>
      </div>

      <>
        <div className={styles.bundlesPurchaseContainer}>
          <ProductInfo
            productTitle={props.bundle.title}
            avgRating={props.bundle.productReviewSummary?.averageRating}
            reviewCount={props.bundle.productReviewSummary?.reviewCount}
            hideSalePrice
            hideWishListIcon
            onReviewSummaryClick={() => {
              pdpStateManager.setShowReviewSection({ reviewSectionInFocus: true })
              const variants =
                bundleConfig.products[0]?.product &&
                productHelper.getVariantsForOptions(
                  bundleConfig.products[0]?.product,
                  bundleConfig.products[0]?.selectedOptions,
                )
              trackEvent(TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK, {
                context: {
                  productProperties:
                    bundleConfig.products[0]?.product &&
                    getProductPropertiesForTracking(
                      bundleConfig.products[0]?.product,
                      variants?.[0]?.shopifyVariantId?.toString(),
                    ),
                },
              })
            }}
            productInfoHeaderTextClassName={styles.headerText}
          />
          <div className={styles.bundlesPurchaseContainer__bundlePrice}>
            <div className={styles.bundlesPurchaseContainer__bundlePrice__pricingDetails}>
              {!isSingleVariantSelectedForEachProduct ? (
                <span className={styles.bundlesPurchaseContainer__bundlePrice__pricingDetails__fromText}>From:</span>
              ) : null}
              <span className={styles.bundlesPurchaseContainer__bundlePrice__pricingDetails__preDiscountPrice}>
                {currency}
                {formatDisplayAmountValue(totalPrice)}
              </span>
              <span className={styles.bundlesPurchaseContainer__bundlePrice__pricingDetails__discountedPrice}>
                {currency}
                {bundlePrice}
              </span>
              <div
                data-testid={TEST_LOCATORS.BUNDLE_DISCOUNT}
                className={styles.bundlesPurchaseContainer__bundlePrice__pricingDetails__discount}
              >
                {discount}% off bundles
              </div>
            </div>
            {traditionalDiscount && (
              <div className={styles.bundlesPurchaseContainer__bundlePrice__traditionalDiscount}>
                {traditionalDiscount}% off traditional retail
              </div>
            )}
          </div>

          {typeof props.bundle.description === 'string' ? (
            <ReadMore className={styles.bundlesPurchaseContainer__bundleDescription}>
              {props.bundle.description}
            </ReadMore>
          ) : null}

          <div data-testid={TEST_LOCATORS.BUNDLE_PRODUCT_LIST} className={styles.bundleProductsWrapper}>
            {bundleConfig.products.map((productConfig, index) => {
              return (
                <BundleProductCard
                  product={productConfig.product}
                  key={`${productConfig.product.id}_${index}`}
                  selectedOptions={productConfig.selectedOptions}
                  onOptionSelect={(optionName, optionValue) => {
                    onOptionSelect(index, optionName, optionValue, false)
                  }}
                  salePrice={formatDisplayAmountValue(productConfig.salePrice)}
                  currency={currency}
                  discount={discount}
                  selectedSiblingProductId={productConfig.product.shopifyProductId}
                  error={productConfig.error}
                  onSiblingOptionSelect={val => {
                    handleSiblingOptionSelect(index, +val)
                  }}
                  showSizeChart={() => {
                    showSizeChart(
                      productConfig.product.sizeChart,
                      productConfig.product.mergedSizeChart,
                      productConfig.product.measuringGuides,
                    )
                  }}
                />
              )
            })}
          </div>
          {Boolean(bundleConfig.optionalProducts.length) && (
            <div className={styles.addOnBundleProductsWrapper}>
              <div className={styles.addOnBundleProductsWrapper__discountText}>
                <Icon iconSvg={DiscountIcon} className={styles.addOnBundleProductsWrapper__discountText__discountSvg} />
                Save {discount}% on extras {`(optional)`}
              </div>
              <div>
                {bundleConfig.optionalProducts.map((productConfig, index) => {
                  return (
                    <BundleProductCard
                      isAddOnProduct
                      product={productConfig.product}
                      key={`${productConfig.product.id}_${index}`}
                      selectedOptions={productConfig.selectedOptions}
                      onOptionSelect={(optionName, optionValue) => {
                        onOptionSelect(index, optionName, optionValue, true)
                      }}
                      expanded={productConfig.selection.isSelected}
                      setExpanded={checked => {
                        handleAddOnProductChecked(index, checked)
                      }}
                      salePrice={formatDisplayAmountValue(productConfig.salePrice)}
                      currency={currency}
                      discount={discount}
                      error={productConfig.error}
                      selectedSiblingProductId={productConfig.product.shopifyProductId}
                      onSiblingOptionSelect={val => {
                        handleSiblingOptionSelect(index, +val, true)
                      }}
                      showSizeChart={() => {
                        showSizeChart(
                          productConfig.product.sizeChart,
                          productConfig.product.mergedSizeChart,
                          productConfig.product.measuringGuides,
                        )
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )}
          <div className={styles.bundlesPurchaseContainer__cartButtonWrapper} ref={addToCartRef}>
            {(errorMsg || errorMessageComingFromShopify) &&
              renderErrorMessage(errorMsg || errorMessageComingFromShopify, styles.errorMessage)}
            <Button
              className={classNames(
                styles.bundlesPurchaseContainer__cartButton,
                isBuyNowInProgress && 'pointer-events-none',
              )}
              type={BUTTON_TYPES.PRIMARY}
              size={BUTTON_SIZE.LARGE}
              disabled={false}
              round={true}
              onClick={() => handleAddToCart()}
              showLoader={isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              id={TEST_LOCATORS.BUNDLE_ADD_TO_CART}
            >
              ADD TO CART {isSingleVariantSelectedForEachProduct && `- ${currency}${bundlePrice}`}
            </Button>
            <If condition={showBuyNowButton}>
              <Button
                className={classNames(
                  styles.bundlesPurchaseContainer__cartButton,
                  'margin-top--2',
                  isAddToCartInProgress && 'pointer-events-none',
                )}
                type={BUTTON_TYPES.OUTLINED}
                size={BUTTON_SIZE.LARGE}
                disabled={false}
                round={true}
                onClick={() => handleAddToCart(showBuyNowButton)}
                showLoader={isBuyNowInProgress}
                selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              >
                Buy Now
              </Button>
            </If>
          </div>
        </div>
        <SizeChart
          show={Boolean(sizeChartConfig?.show && (sizeChartConfig.sizeChart || sizeChartConfig.mergedSizeChart))}
          sizeChart={sizeChartConfig?.sizeChart}
          mergedSizeChart={sizeChartConfig?.mergedSizeChart}
          onCloseSizeChart={hideSizeChart}
        />
        {sizeChartConfig?.sizeChart && (
          <SizeGuide
            showSizeChartModal={Boolean(
              sizeChartConfig.show && (sizeChartConfig.sizeChart || sizeChartConfig.mergedSizeChart),
            )}
            sizeChart={sizeChartConfig.sizeChart}
            mergedSizeChart={sizeChartConfig.mergedSizeChart || []}
            onSizeChartModalClose={hideSizeChart}
            measuringGuides={sizeChartConfig.measuringGuides || []}
          />
        )}
        {showFloatingAddToCartMobile && (
          <AddToCart
            show={true}
            {...(showBuyNowButton && { onBuyNowCart: () => handleAddToCart(showBuyNowButton) })}
            onAddToCart={() => handleAddToCart()}
            salePrice={+bundlePrice}
            traditionalPrice={traditionalPrice}
            isAddToCartInProgress={isAddToCartInProgress}
            selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
          />
        )}
      </>
    </div>
  )
}

export default BundleProductPurchase
